import { DEFAULT_RECEIPT_TYPES } from '@/config/receipt-types';
import { authFetch } from '../lib/auth-fetch';
import { getBackendUrl } from '@/lib/api-config';

export type ReceiptTypeDto = {
  id: string;
  name: string;
  color: string;
  textColor: string;
  default?: boolean;
  ocrModel?: string;
}

// Demo data storage (in-memory)
let demoReceiptTypes: ReceiptTypeDto[] = [...DEFAULT_RECEIPT_TYPES];

export const receiptTypeService = {
  async getAll(isLiveMode: boolean = false): Promise<ReceiptTypeDto[]> {
    if (!isLiveMode) {
      // Return demo data
      return Promise.resolve([...demoReceiptTypes]);
    }
    
    const API_BASE_URL = getBackendUrl();
    const response = await authFetch(`${API_BASE_URL}/api/ReceiptTypes?mode=live`);
    if (!response.ok) throw new Error('Failed to fetch receipt types');
    return await response.json();
  },

  async getById(id: string, isLiveMode: boolean = false): Promise<ReceiptTypeDto | null> {
    if (!isLiveMode) {
      // Return demo data
      const type = demoReceiptTypes.find(t => t.id === id);
      return Promise.resolve(type || null);
    }
    
    const API_BASE_URL = getBackendUrl();
    const response = await authFetch(`${API_BASE_URL}/api/ReceiptTypes/${id}?mode=live`);
    if (!response.ok) return null;
    return await response.json();
  },

  async create(receiptType: Omit<ReceiptTypeDto, 'id'>, isLiveMode: boolean = false): Promise<ReceiptTypeDto> {
    if (!isLiveMode) {
      // Create in demo data
      const newType: ReceiptTypeDto = {
        id: `demo-${Date.now()}`,
        ...receiptType
      };
      demoReceiptTypes = [...demoReceiptTypes, newType];
      return Promise.resolve(newType);
    }
    
    const API_BASE_URL = getBackendUrl();
    const url = `${API_BASE_URL}/api/ReceiptTypes?mode=live`;
    const response = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(receiptType)
    });
    if (!response.ok) throw new Error('Failed to create receipt type');
    return await response.json();
  },

  async update(id: string, receiptType: Omit<ReceiptTypeDto, 'id'>, isLiveMode: boolean = false): Promise<void> {
    if (!isLiveMode) {
      // Update in demo data
      demoReceiptTypes = demoReceiptTypes.map(t => 
        t.id === id ? { id, ...receiptType } : t
      );
      return Promise.resolve();
    }
    
    const API_BASE_URL = getBackendUrl();
    const response = await authFetch(`${API_BASE_URL}/api/ReceiptTypes/${id}?mode=live`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...receiptType })
    });
    if (!response.ok) throw new Error('Failed to update receipt type');
  },

  async delete(id: string, isLiveMode: boolean = false): Promise<void> {
    if (!isLiveMode) {
      // Delete from demo data
      demoReceiptTypes = demoReceiptTypes.filter(t => t.id !== id);
      return Promise.resolve();
    }
    
    const API_BASE_URL = getBackendUrl();
    const response = await authFetch(`${API_BASE_URL}/api/ReceiptTypes/${id}?mode=live`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete receipt type');
  },

  // Reset demo data to defaults
  resetDemoData(): void {
    demoReceiptTypes = [...DEFAULT_RECEIPT_TYPES];
  }
};
